import { Link } from 'react-router-dom';
import PageLayout from '@/components/layout/PageLayout';
import SeoHead from '@/components/seo/SeoHead';

const WHY_ADOPT = [
  {
    title: 'Reduce documentation burden',
    description: 'Clinicians spend significant time writing notes after sessions. AI-assisted drafts across 7 clinical formats — IAR, SOAP, DAP, BIRP, GIRP, PIRP and PIE — give them a structured starting point, reviewed and edited before use, never autonomous.',
  },
  {
    title: 'Maintain clinician consistency',
    description: 'Structured workflow templates support consistent documentation standards across your team without forcing clinicians into a rigid process.',
  },
  {
    title: 'Controlled adoption of AI assistance',
    description: 'Every draft is reviewed and approved by a clinician before it becomes part of the clinical record. AI assists — it does not decide.',
  },
  {
    title: 'Service-level implementation support',
    description: 'Notematic is rolled out with guided implementation support, not dropped in as a consumer app. Onboarding, workflow alignment, and rollout planning are part of the process.',
  },
];

const WORKFLOW_STEPS = [
  { step: '01', title: 'Secure transcription', description: 'Session audio is transcribed securely within the workflow. Audio is processed in-memory and never stored persistently.' },
  { step: '02', title: 'AI-assisted draft generation', description: 'A structured clinical draft is generated from the transcription in your chosen format — IAR, SOAP, DAP, BIRP, GIRP, PIRP or PIE — ready for clinician review, not direct submission.' },
  { step: '03', title: 'Clinician review and edit', description: 'The clinician reviews the draft, edits where needed, and retains full authorship and clinical responsibility.' },
  { step: '04', title: 'Approve or reject', description: 'The clinician approves the final note before export. Rejected drafts are not exported. Every action is logged in the audit trail.' },
];

export default function MmhcPage() {
  return (
    <PageLayout>
      <SeoHead
        title="AI Clinical Documentation for Medicare Mental Health Centres | Notematic"
        description="Notematic helps Medicare Mental Health Centres reduce clinical documentation burden with AI-assisted drafts across 7 formats (IAR, SOAP, DAP, BIRP, GIRP, PIRP, PIE), clinician review controls, and governance-first rollout. Book a demo today."
        canonical="/solutions/mmhc"
      />
      {/* Hero */}
      <section className="section-padding bg-gradient-to-b from-white to-secondary-50 dark:from-secondary-900 dark:to-secondary-800">
        <div className="container-custom text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-primary-50 dark:bg-primary-950 text-primary-700 dark:text-primary-300 text-sm font-medium mb-8">
            Solutions for Medicare Mental Health Centres
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-secondary-900 dark:text-white mb-6">
            AI-assisted documentation built for{' '}
            <span className="text-gradient">Medicare Mental Health Centres</span>
          </h1>
          <p className="text-lg text-secondary-600 dark:text-secondary-300 mb-10">
            Reduce documentation burden with clinician-reviewed drafts across 7 clinical note formats,
            secure transcription, and a rollout model designed for governance-conscious mental health services.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="btn btn-primary btn-lg">Book Demo</Link>
            <Link to="/contact" className="btn btn-outline btn-lg">Request Pilot</Link>
          </div>
        </div>
      </section>

      {/* Why MMHCs adopt */}
      <section className="section-padding bg-white dark:bg-secondary-950">
        <div className="container-custom">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-3xl font-bold text-secondary-900 dark:text-white mb-4">
              Why MMHCs adopt Notematic
            </h2>
            <p className="text-secondary-600 dark:text-secondary-400">
              Notematic is designed for centres that want the benefits of AI-assisted documentation
              without giving up clinical control, rollout discipline, or operational trust.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {WHY_ADOPT.map((item) => (
              <div key={item.title} className="rounded-xl bg-secondary-50 dark:bg-secondary-900 p-6 border border-secondary-200 dark:border-secondary-700">
                <h3 className="font-semibold text-secondary-900 dark:text-white mb-2">{item.title}</h3>
                <p className="text-sm text-secondary-600 dark:text-secondary-400">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How the workflow works */}
      <section className="section-padding bg-secondary-50 dark:bg-secondary-900">
        <div className="container-custom max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-secondary-900 dark:text-white mb-4">
              How the workflow works
            </h2>
            <p className="text-secondary-600 dark:text-secondary-400">
              Every session follows a structured, clinician-controlled process. No autonomous outputs.
              No unreviewed exports.
            </p>
          </div>
          <div className="space-y-6">
            {WORKFLOW_STEPS.map((s) => (
              <div key={s.step} className="flex gap-6 items-start">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary-600 text-white flex items-center justify-center font-bold text-sm">
                  {s.step}
                </div>
                <div>
                  <h3 className="font-semibold text-secondary-900 dark:text-white mb-1">{s.title}</h3>
                  <p className="text-sm text-secondary-600 dark:text-secondary-400">{s.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why safer than generic AI */}
      <section className="section-padding bg-white dark:bg-secondary-950">
        <div className="container-custom max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-secondary-900 dark:text-white mb-6">
            Safer than generic AI tooling
          </h2>
          <p className="text-secondary-600 dark:text-secondary-400 mb-8 text-lg">
            Notematic is designed for teams that need AI assistance to fit real clinical governance,
            not bypass it. The product is built around review-first documentation workflows and
            organisational rollout — not autonomous output or casual self-serve use.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-left">
            {[
              'Clinician-reviewed drafts only',
              'Consent-aware workflow design',
              'Tenant-scoped data separation',
              'Audit trail on every action',
              'No autonomous clinical decisions',
              'Australian-hosted infrastructure',
            ].map((point) => (
              <div key={point} className="flex items-center gap-2 text-sm text-secondary-700 dark:text-secondary-300">
                <svg className="w-4 h-4 text-primary-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                {point}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing preview */}
      <section className="section-padding bg-secondary-50 dark:bg-secondary-900">
        <div className="container-custom max-w-2xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-secondary-900 dark:text-white mb-4">
            Predictable organisational pricing
          </h2>
          <p className="text-secondary-600 dark:text-secondary-400 mb-8">
            No per-session invoicing. Simple monthly costs that are easy to explain through procurement.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            <div className="rounded-xl bg-white dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-700 p-6">
              <div className="text-lg font-bold text-secondary-900 dark:text-white">Practice</div>
              <div className="text-3xl font-bold text-primary-600 my-2">AUD 999<span className="text-sm text-secondary-500">/mo</span></div>
              <div className="text-sm text-secondary-500">5 clinician seats · 1 auditor seat · Unlimited sessions</div>
            </div>
            <div className="rounded-xl bg-primary-600 text-white p-6">
              <div className="text-xs font-semibold uppercase tracking-wider text-primary-100 mb-1">Most Popular</div>
              <div className="text-lg font-bold">Centre</div>
              <div className="text-3xl font-bold my-2">AUD 1,799<span className="text-sm text-primary-200">/mo</span></div>
              <div className="text-sm text-primary-100">10 clinician seats · 2 auditor seats · Unlimited sessions</div>
            </div>
          </div>
          <Link to="/pricing" className="text-primary-600 dark:text-primary-400 text-sm font-medium hover:underline">
            View full pricing details →
          </Link>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section-padding bg-white dark:bg-secondary-950">
        <div className="container-custom max-w-2xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-secondary-900 dark:text-white mb-4">
            Ready to explore a pilot?
          </h2>
          <p className="text-secondary-600 dark:text-secondary-400 mb-8">
            Book a demo to see how Notematic fits your centre's documentation workflow,
            or request a guided pilot program for your team.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="btn btn-primary btn-lg">Book Demo</Link>
            <Link to="/contact" className="btn btn-outline btn-lg">Request Pilot</Link>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}

